import { FunctionComponent, ReactNode, useState } from "react";

import {
	faChevronDown,
	faCircleCheck,
	faCircleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

type SectionStatus = "done" | "missing";

const StatusPill: FunctionComponent<{ status: SectionStatus }> = ({ status }) =>
	status === "done" ? (
		<span className="inline-flex items-center gap-1 rounded-full bg-status-success-light px-2 py-0.5 text-xs font-semibold text-status-success">
			<Icon className="w-3 h-3" icon={faCircleCheck} />
			Defined
		</span>
	) : (
		<span className="inline-flex items-center gap-1 rounded-full bg-status-error-light px-2 py-0.5 text-xs font-semibold text-status-error">
			<Icon className="w-3 h-3" icon={faCircleExclamation} />
			Missing information
		</span>
	);

/**
 * One collapsible card section of the campaign settings modal (Campaign / Schedule / Senders),
 * replacing the old MUI tabs. Shows a title, a Defined / Missing-information status pill, an
 * optional right-aligned adornment (e.g. the senders count badge), and a chevron.
 */
export const CampaignSettingsSection: FunctionComponent<{
	title: string;
	status: SectionStatus;
	defaultOpen?: boolean;
	headerRight?: ReactNode;
	children: ReactNode;
}> = ({ title, status, defaultOpen = true, headerRight, children }) => {
	const [open, setOpen] = useState<boolean>(defaultOpen);

	return (
		<div className="overflow-hidden rounded-xl border border-border-light bg-white">
			<button
				className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left"
				type="button"
				onClick={() => setOpen((value) => !value)}
			>
				<span className="text-sm font-semibold text-gray-900">{title}</span>
				<StatusPill status={status} />
				<span className="flex-1" />
				{headerRight}
				<Icon
					className={classNames(
						"text-text-meta transition-transform duration-200",
						!open && "-rotate-90"
					)}
					icon={faChevronDown}
				/>
			</button>
			{open && <div className="border-t border-border-light px-4 py-4">{children}</div>}
		</div>
	);
};
